/* eslint-disable @atlaskit/volt-strict-mode/no-multiple-exports */
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as createUUID } from 'uuid';

import { fg } from '@atlaskit/platform-feature-flags';

import coinflip from '../coinflip';
import type {
	AbortReasonType,
	ApdexType,
	BM3Event,
	CustomData,
	CustomTiming,
	InteractionError,
	HoldActive,
	HoldInfo,
	InteractionMetrics,
	InteractionType,
	MetricVariantCategory,
	LifecycleMarkType,
	LoadProfilerEventInfo,
	Mark,
	MarkType,
	PostInteractionLogOutput,
	ReactProfilerTiming,
	RequestInfo,
	SegmentInfo,
	Segment3pTimingEntry,
	Span,
	SpanType,
} from '../common';
import { sanitizeTimingName } from '../common/utils/timing-name';
import {
	getAwaitBM3TTIList,
	getCapabilityRate,
	getConfig,
	getExtraInteractionRate,
	getFinishInteractionOnTransition,
	getInteractionTimeout,
	getPostInteractionRate,
	isInteractionExtraMetricsEnabled,
	getReactHydrationStats,
	getSelectorConfig,
	shouldUseRawDataThirdPartyBehavior,
} from '../config';
import { onSearchPageInteractionComplete } from '../create-extra-search-page-interaction-payload';
import { sanitizeUfoName, stringifyLabelStackFully } from '../create-payload/common/utils';
import { clearActiveTrace, type TraceIdContext } from '../experience-trace-id-context';
import {
	allFeatureFlagsAccessed,
	currentFeatureFlagsAccessed,
	type FeatureFlagValue,
} from '../feature-flags-accessed';
import type { LabelStack, SegmentLabel } from '../interaction-context';
import { getInteractionId } from '../interaction-id-context';
import { BACKEND_RESOURCE_TIMING_INITIATOR_TYPES } from '../resource-timing/common/utils/resource-timing-initiator-types';
import { flushSsrRenderProfilerTraces } from '../segment/ssr-render-profiler';
import { newVCObserver } from '../vc';
import { type VCObserverInterface } from '../vc/types';

import { interactions } from './common/constants';
import InteractionExtraMetrics from './interaction-extra-metrics';
import PostInteractionLog from './post-interaction-log';

export type {
	InteractionMetrics,
	LifecycleMarkType,
	Span,
	Mark,
	MarkType,
	InteractionType,
	AbortReasonType,
	ReactProfilerTiming,
	RequestInfo,
	ApdexType,
	CustomData,
	CustomTiming,
	InteractionError,
};

export type PreviousInteractionLogType = {
	id: string | undefined;
	name: string | undefined;
	type: string | undefined;
	isAborted: boolean | undefined;
	timestamp: number | undefined;
};

export const PreviousInteractionLog: PreviousInteractionLogType = {
	id: undefined,
	name: undefined,
	type: undefined,
	isAborted: undefined,
	timestamp: undefined,
};

export const postInteractionLog: PostInteractionLog = new PostInteractionLog();
export const interactionExtraMetrics: InteractionExtraMetrics = new InteractionExtraMetrics();

const interactionQueue: { id: string; data: InteractionMetrics }[] = [];
const segmentCache = new Map<string, SegmentInfo>();
export const segmentUnmountCache: Map<string, number> = new Map<string, number>(); // Temporarily store segment unmount counts

let firstSegmentLoadMarked = false;

interface SegmentObserver {
	onAdd: (segment: SegmentInfo) => void;
	onRemove: (segment: SegmentInfo) => void;
}
const segmentObservers: SegmentObserver[] = [];

export function getActiveInteraction(): InteractionMetrics | undefined {
	const interactionId = getInteractionId();
	if (!interactionId.current) {
		return;
	}
	return interactions.get(interactionId.current);
}

function isPerformanceTracingEnabled() {
	return (
		getConfig()?.enableAdditionalPerformanceMarks ||
		window.__REACT_UFO_ENABLE_PERF_TRACING ||
		process.env.NODE_ENV !== 'production'
	);
}

let performanceEventObserver: PerformanceObserver | undefined;

export const getPerformanceObserver = (): PerformanceObserver => {
	performanceEventObserver =
		performanceEventObserver ||
		new PerformanceObserver((entries: PerformanceObserverEntryList) => {
			const list = entries.getEntries();
			for (let entry of list) {
				if (entry.name === 'click') {
					setInteractionPerformanceEvent(entry as PerformanceEventTiming);
				}
			}
		});
	return performanceEventObserver;
};

export const setInteractionPerformanceEvent = (entry: PerformanceEventTiming): void => {
	const interaction = getActiveInteraction();
	if (interaction?.type === 'press') {
		const responsiveness = interaction.responsiveness || {};
		// if happens there is another event interaction that has started after
		// the initial one, we don't want to replace the values if they have already
		// been set up
		responsiveness.experimentalInputToNextPaint =
			responsiveness.experimentalInputToNextPaint || entry.duration;
		responsiveness.inputDelay =
			responsiveness.experimentalInputToNextPaint || entry.processingStart - entry.startTime;
		interaction.responsiveness = responsiveness;
		// if the entry start time is lower than the one in the interaction
		// it means the interaction start time is not accurate, we assign
		// this value which will match the timestamp in the event
		if (entry.startTime < interaction.start) {
			interaction.start = entry.startTime;
		}
	}
};

function labelStackToString(labelStack: LabelStack | null | undefined, name?: string) {
	const stack = [...(labelStack ?? [])];
	if (name) {
		stack.push({ name });
	}
	return stack.map((l) => l.name)?.join('/');
}

function labelStackToIdString(labelStack: LabelStack | null | undefined) {
	return labelStack
		?.map((l) => ('segmentId' in l ? `${l.name}:${l.segmentId}` : `${l.name}`))
		?.join('/');
}

function addSegmentObserver(observer: SegmentObserver) {
	segmentObservers.push(observer);

	for (const segmentInfo of segmentCache.values()) {
		observer.onAdd(segmentInfo);
	}
}

function removeSegmentObserver(observer: SegmentObserver) {
	const index = segmentObservers.findIndex((obs) => obs === observer);

	if (index !== -1) {
		segmentObservers.splice(index, 1);
	}
}

export function remove(interactionId: string): void {
	interactions.delete(interactionId);
}

export function updatePageLoadInteractionName(
	ufoName: string,
	routeName: string | null | undefined = ufoName,
): void {
	const interaction = getActiveInteraction();
	if (!interaction || (interaction.type !== 'page_load' && interaction.type !== 'transition')) {
		return;
	}
	interaction.ufoName = ufoName;
	interaction.routeName = routeName;
}

export function addMetadata(interactionId: string, data: Record<string, unknown>): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		Object.keys(data).forEach((key) => {
			interaction.metaData[key] = data[key];
		});
	}
}

export function addCustomData(
	interactionId: string,
	labelStack: LabelStack,
	data: CustomData,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		Object.keys(data).forEach((i) => {
			interaction.customData.push({ labelStack, data: { [i]: data[i] } });
		});
	}
}

export function addSegmentExtraData(
	interactionId: string,
	segmentId: string,
	data: Record<string, string | undefined>,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		if (!interaction.segmentExtraData) {
			interaction.segmentExtraData = {};
		}
		interaction.segmentExtraData[segmentId] = {
			...interaction.segmentExtraData[segmentId],
			...data,
		};
	}
}

/**
 * Builds a stable cross-segment dedup key for a segment3pTimings entry.
 *
 * For `resource-timing`, query-stripped backend labels + timing fields identify a request without
 * depending on query parameters. Shared CSS/JS asset timings dedupe across segments, but backend
 * fetch/XHR entries are scoped by `segmentId` because each iframe can make its own backend request
 * to the same URL.
 *
 * For all other labels the data objects are small (a handful of fields), so we use a full
 * JSON fingerprint which is cheap and collision-free.
 */
function buildCrossSegmentDedupKey(entry: Segment3pTimingEntry, segmentId: string): string {
	if (entry.label === 'resource-timing') {
		const {
			label: resourceLabel,
			startTime,
			duration,
			requestStart,
			fetchStart,
			ttfb,
			type,
		} = entry.data as Record<string, unknown>;
		if (BACKEND_RESOURCE_TIMING_INITIATOR_TYPES.has(String(type))) {
			// Backend calls are separate requests per segment; include extra timings and segment scope.
			return `resource-timing|${resourceLabel}|${startTime}|${duration}|${fetchStart}|${requestStart}|${ttfb}|${segmentId}`;
		}
		// Shared CSS/JS assets keep the original B3 key so they can dedupe across segments.
		return `resource-timing|${resourceLabel}|${startTime}|${duration}`;
	}
	return `${entry.label}|${JSON.stringify(entry.data)}`;
}

export function addIframeSegmentData(
	interactionId: string,
	segmentId: string,
	entry: Segment3pTimingEntry,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null && fg('platform_ufo_3p_segment_timings')) {
		// B3: dedup by content — drops identical entries within and across iframes.
		// Abort markers are excluded so every segment's abort is recorded.
		if (entry.label !== 'segment-timing-abort') {
			if (!interaction.segment3pCrossSegmentSeen) {
				interaction.segment3pCrossSegmentSeen = new Set();
			}
			const key = buildCrossSegmentDedupKey(entry, segmentId);
			if (interaction.segment3pCrossSegmentSeen.has(key)) {
				return;
			}
			interaction.segment3pCrossSegmentSeen.add(key);
		}

		if (!interaction.segment3pTimings) {
			interaction.segment3pTimings = {};
		}
		if (!interaction.segment3pTimings[segmentId]) {
			interaction.segment3pTimings[segmentId] = [];
		}
		interaction.segment3pTimings[segmentId].push(entry);
	}
}

export function addCohortingCustomData(
	interactionId: string,
	key: string,
	value: number | boolean | string | null | undefined,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction == null) {
		return;
	}

	// Allow null and undefined values
	if (value === null || value === undefined) {
		interaction.cohortingCustomData.set(key, value);
		return;
	}

	// Validate that the value is a primitive (number, boolean, or string)
	if (typeof value !== 'number' && typeof value !== 'boolean' && typeof value !== 'string') {
		return;
	}

	// Validate string length (max 50 characters)
	if (typeof value === 'string' && value.length > 50) {
		return;
	}

	interaction.cohortingCustomData.set(key, value);
}

export function addCustomTiming(
	interactionId: string,
	labelStack: LabelStack,
	data: CustomTiming,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const sanitizedData = Object.fromEntries(
			Object.entries(data).map(([key, timingData]) => [sanitizeTimingName(key), timingData]),
		) as CustomTiming;
		interaction.customTimings.push({ labelStack, data: sanitizedData });
		if (isPerformanceTracingEnabled()) {
			for (const [key, timingData] of Object.entries(sanitizedData)) {
				const { startTime, endTime } = timingData;
				try {
					// for Firefox 102 and older
					performance.measure(`🛸 ${labelStackToString(labelStack, key)} [custom_timing]`, {
						start: startTime,
						end: endTime,
						detail: {
							devtools: {
								track: '🛸 reactUFO detailed timings',
								color: 'tertiary-light',
							},
						},
					});
				} catch {
					// do nothing
				}
			}
		}
	}
}

export function addMark(
	interactionId: string,
	type: MarkType,
	name: string,
	labelStack: LabelStack | null,
	time: number = performance.now(),
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.marks.push({ type, name, labelStack, time });
	}
	if (isPerformanceTracingEnabled()) {
		performance.mark(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
			startTime: time,
		});
	}
}

export function markFirstSegmentLoad(
	interactionId: string,
	labelStack: LabelStack | null,
	time: number = performance.now(),
): void {
	if (!fg('platform_mark_ufo_segment_first_load')) {
		return;
	}
	const interaction = interactions.get(interactionId);
	if (interaction != null && !firstSegmentLoadMarked) {
		firstSegmentLoadMarked = true;
		interaction.marks.push({
			type: 'first_segment_load',
			name: 'first_segment_load',
			labelStack,
			time,
		});
	}
}

export function addMarkToAll(
	type: MarkType,
	name: string,
	labelStack: LabelStack | null,
	time: number = performance.now(),
): void {
	interactions.forEach((interaction) => {
		interaction.marks.push({ type, name, labelStack, time });
	});
	if (isPerformanceTracingEnabled()) {
		performance.mark(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
			startTime: time,
		});
	}
}

export function addSpan(
	interactionId: string,
	type: SpanType,
	name: string,
	labelStack: LabelStack | null,
	start: number,
	end: number = performance.now(),
	size?: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.spans.push({ type, name, labelStack, start, end, size });
		if (isPerformanceTracingEnabled()) {
			try {
				// for Firefox 102 and older
				performance.measure(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
					start,
					end,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'secondary',
						},
					},
				});
			} catch {
				// do nothing
			}
		}
	}
}

export function addSpanToAll(
	type: SpanType,
	name: string,
	labelStack: LabelStack | null,
	start: number,
	end: number = performance.now(),
	size = 0,
): void {
	interactions.forEach((interaction) => {
		interaction.spans.push({ type, name, labelStack, start, end, size });
	});
	if (isPerformanceTracingEnabled()) {
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${labelStackToString(labelStack, name)} [${type}]`, {
				start,
				end,
				detail: {
					devtools: {
						track: '🛸 reactUFO detailed timings',
						color: 'secondary',
					},
				},
			});
		} catch {
			// do nothing
		}
	}
}

export function addPreload(moduleId: string, timestamp: number): void {
	addMarkToAll('bundle_preload', moduleId, null, timestamp);
}

export function addLoad(identifier: string, start: number, end: number): void {
	addSpanToAll('bundle_load', identifier, null, start, end - start);
}

const moduleLoadingRequests: Record<
	string,
	{
		start: number;
		timeoutId: ReturnType<typeof setTimeout> | number | undefined;
	}
> = {};

export function extractModuleName(input: string): string {
	let result = input ?? '';

	result = result.replace(/^\.\/src\/packages\//, '');
	result = result.replace(/^\.\/node_modules\//, '');
	result = result.replace(/(\/src)?\/(index|main)\.(tsx|ts|js|jsx)$/, '');

	return result;
}

function addHoldCriterion(id: string, labelStack: LabelStack, name: string, startTime: number) {
	if (!window.__CRITERION__?.addUFOHold) {
		return;
	}
	window.__CRITERION__.addUFOHold(id, labelStackToString(labelStack), name, startTime);
}

function removeHoldCriterion(id: string) {
	if (!window.__CRITERION__?.removeUFOHold) {
		return;
	}
	window.__CRITERION__.removeUFOHold(id);
}

export function addHold(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	_experimental: boolean,
): () => void {
	const interaction = interactions.get(interactionId);
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const id = createUUID();

	if (!interaction) {
		// add hold timestamp to post interaction log if interaction is complete
		postInteractionLog.addHoldInfo(labelStack, name, performance.now());
	}

	if (interaction != null) {
		const start = performance.now();
		const holdActive = { labelStack, name, start };

		if (shouldMoveHoldToExtendedBucket(interaction, holdActive)) {
			ensureExtendedHoldActive(interaction).set(id, { ...holdActive, start });
		} else {
			interaction.holdActive.set(id, { ...holdActive, start });
			addHoldCriterion(id, labelStack, name, start);
		}

		return () => {
			const end = performance.now();
			if (isPerformanceTracingEnabled()) {
				try {
					// for Firefox 102 and older
					performance.measure(`🛸 ${labelStackToString(labelStack, name)} [hold]`, {
						start,
						end,
						detail: {
							devtools: {
								track: '🛸 reactUFO detailed timings',
								color: 'secondary-light',
							},
						},
					});
				} catch {
					// do nothing
				}
			}
			removeHoldCriterion(id);
			const currentInteraction = interactions.get(interactionId);
			const currentHold = interaction.holdActive.get(id);
			if (currentInteraction != null) {
				if (currentHold != null) {
					currentInteraction.holdInfo.push({ ...currentHold, end });
					interaction.holdActive.delete(id);
				}

				if (interaction.hold3pActive) {
					const current3pHold = interaction.hold3pActive.get(id);
					if (current3pHold != null) {
						recordMetricVariantCategoryEnd(currentInteraction, current3pHold.labelStack, end);
						if (!currentInteraction.hold3pInfo) {
							currentInteraction.hold3pInfo = [];
						}
						currentInteraction.hold3pInfo.push({ ...current3pHold, end });
						interaction.hold3pActive.delete(id);
					}
				}
			}
		};
	}
	return () => {};
}

export function addHoldByID(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	id: string,
	ignoreOnSubmit?: boolean,
) {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const start = performance.now();
		const holdActive = { labelStack, name, start, ignoreOnSubmit };
		if (shouldMoveHoldToExtendedBucket(interaction, holdActive)) {
			ensureExtendedHoldActive(interaction).set(id, holdActive);
		} else {
			interaction.holdActive.set(id, holdActive);
			addHoldCriterion(id, labelStack, name, start);
		}
	}
	return (): void => {};
}

/**
 * Records a completed third-party hold with explicit start and end timestamps.
 * Use this when the hold timing is known upfront (e.g. from React Profiler's
 * actualStartTime and commitTime) rather than being captured at call time.
 */
export function addCompletedHold(
	interactionId: string,
	labelStack: LabelStack,
	name: string,
	start: number,
	end: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		recordMetricVariantCategoryEnd(interaction, labelStack, end);
		ensureExtendedHoldInfo(interaction).push({ labelStack, name, start, end });
	}
}

export function removeHoldByID(interactionId: string, id: string): void {
	const interaction = interactions.get(interactionId);

	if (interaction != null) {
		const end = performance.now();
		const currentInteraction = interactions.get(interactionId);
		const currentHold = interaction.holdActive.get(id);
		if (currentInteraction != null && currentHold != null) {
			if (shouldMoveHoldToExtendedBucket(currentInteraction, currentHold)) {
				ensureExtendedHoldInfo(currentInteraction).push({ ...currentHold, end });
			} else {
				currentInteraction.holdInfo.push({ ...currentHold, end });
			}
			interaction.holdActive.delete(id);
			removeHoldCriterion(id);
		}

		if (interaction.hold3pActive) {
			const current3pHold = interaction.hold3pActive.get(id);
			if (currentInteraction != null && current3pHold != null) {
				recordMetricVariantCategoryEnd(currentInteraction, current3pHold.labelStack, end);
				if (!currentInteraction.hold3pInfo) {
					currentInteraction.hold3pInfo = [];
				}
				currentInteraction.hold3pInfo.push({ ...current3pHold, end });
				interaction.hold3pActive.delete(id);
			}
		}
	}
}

export function getCurrentInteractionType(interactionId: string): InteractionType | null {
	const interaction = interactions.get(interactionId);
	if (interaction) {
		return interaction.type;
	}
	return null;
}

export const ModuleLoadingProfiler = {
	onPreload(moduleId: string, _priority?: number): void {
		addPreload(extractModuleName(moduleId), performance.now());
	},
	onLoadStart(info: LoadProfilerEventInfo): void {
		const timeoutId = setTimeout(() => {
			delete moduleLoadingRequests[info.identifier];
		}, 30000);
		const request = {
			start: performance.now(),
			timeoutId,
		};
		moduleLoadingRequests[info.identifier] = request;
	},
	onLoadComplete(info: LoadProfilerEventInfo): void {
		const request = moduleLoadingRequests[info.identifier];
		if (request) {
			clearTimeout(request.timeoutId);
			delete moduleLoadingRequests[info.identifier];
			addLoad(extractModuleName(info.identifier), request.start, performance.now());
		}
	},
	placeholderFallBackMounted(id: string, moduleId: string): void {
		const interactionId = getInteractionId();
		const currentInteractionId = interactionId.current;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		addHoldByID(currentInteractionId!, [], moduleId, id);
	},
	placeholderFallBackUnmounted(id: string): void {
		const interactionId = getInteractionId();
		const currentInteractionId = interactionId.current;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		removeHoldByID(currentInteractionId!, id);
	},
};

export function addError(
	interactionId: string,
	name: string,
	labelStack: LabelStack | null,
	errorType: string,
	errorMessage: string,
	errorStack?: string,
	forcedError?: boolean,
	errorHash?: string,
	errorStatusCode?: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.errors.push({
			name,
			labelStack,
			errorType,
			errorMessage,
			errorStack,
			forcedError,
			errorHash,
			errorStatusCode,
		});
	}
}

export function addErrorToAll(
	name: string,
	labelStack: LabelStack | null,
	errorType: string,
	errorMessage: string,
	errorStack?: string,
	errorHash?: string,
	errorStatusCode?: number,
): void {
	interactions.forEach((interaction) => {
		interaction.errors.push({
			name,
			labelStack,
			errorType,
			errorMessage,
			errorStack,
			errorHash,
			errorStatusCode,
		});
	});
}

export function addProfilerTimings(
	interactionId: string,
	labelStack: LabelStack,
	type: 'mount' | 'update' | 'nested-update',
	actualDuration: number,
	baseDuration: number,
	startTime: number,
	commitTime: number,
): void {
	if (isPerformanceTracingEnabled()) {
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${labelStackToString(labelStack)} [react-profiler] ${type}`, {
				start: startTime,
				duration: actualDuration,
				detail: {
					devtools: {
						track: '🛸 reactUFO detailed timings',
						color: 'secondary-dark',
					},
				},
			});
		} catch {
			// do nothing
		}
	}
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.reactProfilerTimings.push({
			labelStack,
			type,
			actualDuration,
			baseDuration,
			startTime,
			commitTime,
		});
	} else if (getConfig()?.postInteractionLog?.enabled) {
		postInteractionLog.addProfilerTimings(
			labelStack,
			type,
			actualDuration,
			baseDuration,
			startTime,
			commitTime,
		);
	}
}

function pushToQueue(id: string, data: InteractionMetrics) {
	interactionQueue.push({ id, data });
}

let handleInteraction = pushToQueue;

const isKnownMetricVariantCategory = (type: unknown): type is MetricVariantCategory =>
	type === 'third-party' || type === 'gen-ai';

const isMetricVariantCategoryEnabled = (category: MetricVariantCategory): boolean =>
	category === 'third-party' || fg('platform_ufo_gen_ai_segment');

const hasMetricVariantCategory = (
	labelStack: LabelStack | null | undefined,
	category: MetricVariantCategory,
): boolean =>
	labelStack?.some(
		(label) =>
			'type' in label && label.type === category && isMetricVariantCategoryEnabled(category),
	) ?? false;

const hasAnyMetricVariantCategory = (labelStack: LabelStack | null | undefined): boolean =>
	labelStack?.some(
		(label) =>
			'type' in label &&
			isKnownMetricVariantCategory(label.type) &&
			isMetricVariantCategoryEnabled(label.type),
	) ?? false;

const getMetricVariantCategories = (
	labelStack: LabelStack | null | undefined,
): MetricVariantCategory[] => {
	const categories = new Set<MetricVariantCategory>();
	labelStack?.forEach((label) => {
		if (
			'type' in label &&
			isKnownMetricVariantCategory(label.type) &&
			isMetricVariantCategoryEnabled(label.type)
		) {
			categories.add(label.type);
		}
	});
	return [...categories];
};

const hasMetricVariantCategoryInHoldData = (
	interaction: InteractionMetrics,
	category: MetricVariantCategory,
): boolean =>
	interaction.holdInfo.some((hold) => hasMetricVariantCategory(hold.labelStack, category)) ||
	[...(interaction.holdActive.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	) ||
	[...(interaction.hold3pInfo ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	) ||
	[...(interaction.hold3pActive?.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	);

const recordMetricVariantCategoryEnd = (
	interaction: InteractionMetrics,
	labelStack: LabelStack | null | undefined,
	end: number,
): void => {
	const categories = getMetricVariantCategories(labelStack);
	if (categories.length === 0) {
		return;
	}

	interaction.metricCategoryEnds = interaction.metricCategoryEnds ?? {};
	categories.forEach((category) => {
		interaction.metricCategoryEnds![category] = Math.max(
			interaction.metricCategoryEnds![category] ?? 0,
			end,
		);
	});
};

const recordActiveMetricVariantCategoryEnds = (
	interaction: InteractionMetrics,
	end: number,
): void => {
	interaction.hold3pActive?.forEach((hold) => {
		recordMetricVariantCategoryEnd(interaction, hold.labelStack, end);
	});
};

const hasActiveMetricVariantCategory = (
	interaction: InteractionMetrics,
	category: MetricVariantCategory,
): boolean =>
	[...(interaction.hold3pActive?.values() ?? [])].some((hold) =>
		hasMetricVariantCategory(hold.labelStack, category),
	);

function shouldMoveHoldToExtendedBucket(
	interaction: InteractionMetrics,
	hold: Pick<HoldActive, 'start' | 'labelStack'>,
): boolean {
	return (
		hasAnyMetricVariantCategory(hold.labelStack) ||
		(interaction.end !== 0 && hold.start > interaction.end)
	);
}

function ensureExtendedHoldActive(interaction: InteractionMetrics): Map<string, HoldActive> {
	interaction.hold3pActive = interaction.hold3pActive ?? new Map();
	return interaction.hold3pActive;
}

function ensureExtendedHoldInfo(interaction: InteractionMetrics): HoldInfo[] {
	interaction.hold3pInfo = interaction.hold3pInfo ?? [];
	return interaction.hold3pInfo;
}

function moveLateActiveHoldsToExtendedBucket(interaction: InteractionMetrics): void {
	if (interaction.end === 0) {
		return;
	}

	const holdsToMove = [...interaction.holdActive.entries()].filter(([, hold]) =>
		shouldMoveHoldToExtendedBucket(interaction, hold),
	);

	holdsToMove.forEach(([id, hold]) => {
		ensureExtendedHoldActive(interaction).set(id, hold);
		interaction.holdActive.delete(id);
	});
}

function ensureMetricWindows(interaction: InteractionMetrics): void {
	if (interaction.end === 0) {
		return;
	}

	const shouldExcludeGenAI = fg('platform_ufo_gen_ai_segment');
	const hasGenAI = shouldExcludeGenAI && hasMetricVariantCategoryInHoldData(interaction, 'gen-ai');
	const hasThirdParty = hasMetricVariantCategoryInHoldData(interaction, 'third-party');
	const includeThirdPartyEnd =
		interaction.metricCategoryEnds?.['third-party'] !== undefined
			? Math.max(interaction.metricCategoryEnds['third-party'], interaction.end)
			: interaction.end3p;
	const includeGenAIEnd =
		interaction.metricCategoryEnds?.['gen-ai'] !== undefined
			? Math.max(interaction.metricCategoryEnds['gen-ai'], interaction.end)
			: undefined;

	interaction.metricWindows = {
		...interaction.metricWindows,
		standard: {
			start: interaction.start,
			end: interaction.end,
			includeCategories: [],
			excludeCategories: shouldExcludeGenAI ? ['third-party', 'gen-ai'] : ['third-party'],
		},
	};

	if (includeThirdPartyEnd !== undefined && (hasThirdParty || !hasGenAI)) {
		interaction.metricWindows['include-third-party'] = {
			start: interaction.start,
			end: includeThirdPartyEnd,
			includeCategories: ['third-party'],
			excludeCategories: [],
		};
	}

	if (hasGenAI && includeGenAIEnd !== undefined) {
		interaction.metricWindows['include-gen-ai'] = {
			start: interaction.start,
			end: includeGenAIEnd,
			includeCategories: ['gen-ai'],
			excludeCategories: [],
		};
	}
}

function addLifecycleObservation(
	interaction: InteractionMetrics,
	observation: NonNullable<InteractionMetrics['lifecycleObservations']>[number],
): void {
	interaction.lifecycleObservations = interaction.lifecycleObservations ?? [];
	interaction.lifecycleObservations.push(observation);
}

function callCleanUpCallbacks(interaction: InteractionMetrics) {
	interaction.cleanupCallbacks.reverse().forEach((cleanUpCallback) => {
		cleanUpCallback();
	});
}

function finishInteraction(
	id: string,
	data: InteractionMetrics,
	endTime: number = performance.now(),
) {
	data.end = endTime;
	ensureMetricWindows(data);
	try {
		// for Firefox 102 and older
		performance.measure(`🛸 [${data.type}] ${data.ufoName} [ttai]`, {
			start: data.start,
			end: data.end,
			detail: {
				devtools: {
					track: 'main metrics',
					trackGroup: '🛸 reactUFO metrics',
					color: 'tertiary',
				},
			},
		});

		performance.mark(`TTAI`, {
			startTime: data.end,
			detail: {
				devtools: {
					dataType: 'marker',
				},
			},
		});
	} catch {
		// do nothing
	}
	if (data.featureFlags) {
		data.featureFlags.during = Object.fromEntries(currentFeatureFlagsAccessed);
	}
	clearActiveTrace();
	callCleanUpCallbacks(data);
	flushSsrRenderProfilerTraces();
	if (data.type === 'page_load') {
		data.hydration = getReactHydrationStats();
	}

	// By this time, stop the post interaction log observer if coinflip rate is 0
	const sanitisedUfoName = sanitizeUfoName(data.ufoName);
	if (!coinflip(getPostInteractionRate(sanitisedUfoName, data.type))) {
		postInteractionLog.stopVCObserver();
	}

	if (!isInteractionExtraMetricsEnabled()) {
		remove(id);
	} else if (!coinflip(getExtraInteractionRate(sanitisedUfoName, data.type))) {
		interactionExtraMetrics.stopAll(id);
	} else if (!data.hold3pActive || data.hold3pActive.size === 0) {
		remove(id);
	}

	PreviousInteractionLog.id = data.id;
	PreviousInteractionLog.type = data.type;
	PreviousInteractionLog.timestamp = data.end;
	PreviousInteractionLog.name = data.ufoName || 'unknown';
	PreviousInteractionLog.isAborted = data.abortReason != null;
	if (data.ufoName) {
		if (
			!isInteractionExtraMetricsEnabled() ||
			interactionExtraMetrics.finishedInteraction?.id !== id
		) {
			// If this same interaction was not already handled, handle it
			handleInteraction(id, data);
		}
	}

	if (isPerformanceTracingEnabled()) {
		const profilerTimingMap = new Map<
			string,
			{ labelStack: LabelStack; start?: number; end?: number }
		>();
		data.reactProfilerTimings.forEach((profilerTiming) => {
			const labelStackId = labelStackToIdString(profilerTiming.labelStack);
			if (labelStackId) {
				const timing = profilerTimingMap.get(labelStackId) ?? {
					labelStack: profilerTiming.labelStack,
				};
				timing.start =
					profilerTiming.startTime < (timing.start ?? Number.MAX_SAFE_INTEGER)
						? profilerTiming.startTime
						: timing.start;
				timing.end =
					profilerTiming.commitTime > (timing.end ?? Number.MIN_SAFE_INTEGER)
						? profilerTiming.commitTime
						: timing.end;
				profilerTimingMap.set(labelStackId, timing);
			}
		});
		try {
			// for Firefox 102 and older
			for (const [, { labelStack, start, end }] of profilerTimingMap.entries()) {
				performance.measure(`🛸 ${labelStackToString(labelStack)} [segment_ttai]`, {
					start,
					end,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'secondary-dark',
						},
					},
				});
			}
		} catch {
			// do nothing
		}
	}

	try {
		// dispatch a global window event to notify the measure is completed
		window.dispatchEvent(
			new CustomEvent<InteractionMetrics>('UFO_FINISH_INTERACTION', { detail: data }),
		);
	} catch {
		// do nothing
	}
}

export function sinkInteractionHandler(
	sinkFn: (id: string, data: InteractionMetrics) => void,
): void {
	if (handleInteraction === pushToQueue) {
		handleInteraction = sinkFn;
		interactionQueue.forEach((interaction) => {
			sinkFn(interaction.id, interaction.data);
		});
		interactionQueue.length = 0;
	}
}

export function sinkPostInteractionLogHandler(
	sinkFn: (output: PostInteractionLogOutput) => void | Promise<void>,
): void {
	postInteractionLog.sinkHandler(sinkFn);
}

// a flag to prevent multiple submitting
let activeSubmitted = false;

export function tryComplete(interactionId: string, endTime?: number): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);

		const postInteraction = async () => {
			if (getConfig()?.postInteractionLog?.enabled) {
				postInteractionLog.onInteractionComplete(interaction);
			}
			activeSubmitted = false;
		};

		const noMoreActiveMetricVariantHolds =
			interaction.hold3pActive?.size === 0 || interaction.hold3pActive === undefined;

		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If using separated metric variant behavior, wait for extended category holds to clear.
		// The backing fields still use their legacy 3P names because they are part of the existing
		// third-party payload contract, but this bucket now also drives client metric windows.
		if (shouldUseSeparatedMetricVariantBehavior) {
			// If there are no standard holds active, mark the interaction as successful
			// but don't finish until extended metric variant holds are cleared.
			if (noMoreActiveHolds && !noMoreActiveMetricVariantHolds) {
				// Mark interaction as successful by setting endTime, but don't finish yet
				if (endTime !== undefined && interaction.end === 0) {
					interaction.end = endTime;
				}
				moveLateActiveHoldsToExtendedBucket(interaction);
				ensureMetricWindows(interaction);
				// Wait for extended metric variant holds to clear before finishing.
				return;
			}

			// If all holds, including extended metric variant holds, are cleared, finish the interaction.
			if (noMoreActiveHolds && noMoreActiveMetricVariantHolds) {
				if (!activeSubmitted) {
					// Set the legacy third-party end marker when extended metric variant holds clear,
					// preserving existing 3P payload behavior while category-specific end times are
					// recorded separately in metricCategoryEnds.
					const currentTime = endTime ?? performance.now();
					interaction.end3p =
						interaction.end !== 0 && currentTime < interaction.end ? interaction.end : currentTime;
					ensureMetricWindows(interaction);
					finishInteraction(
						interactionId,
						interaction,
						interaction.end !== 0 ? interaction.end : endTime,
					);
					if (isInteractionExtraMetricsEnabled()) {
						interactionExtraMetrics.updateFinishedInteraction(interaction);
					}

					if (
						getConfig()?.extraSearchPageInteraction?.enabled &&
						interaction.ufoName === getConfig()?.extraSearchPageInteraction?.searchPageMetricName
					) {
						onSearchPageInteractionComplete(interactionId, interaction);
					}

					activeSubmitted = true;
				}

				postInteraction();
			}
			// Send separated third-party event when extra interaction metrics is enabled
			if (
				shouldUseRawDataThirdParty &&
				isInteractionExtraMetricsEnabled() &&
				noMoreActiveHolds &&
				noMoreActiveMetricVariantHolds
			) {
				const data = {
					...interaction,
					end: endTime || interaction.end,
				};
				interactionExtraMetrics.onInteractionComplete(interactionId, data);
			}
		} else {
			// Original behavior when feature flag is not active
			if (
				noMoreActiveHolds &&
				(!isInteractionExtraMetricsEnabled() ||
					interactionExtraMetrics.finishedInteraction?.id !== interactionId)
			) {
				// If it's not waiting for extra metrics to complete, finish the interaction as normal
				if (!activeSubmitted) {
					finishInteraction(interactionId, interaction, endTime);
					if (isInteractionExtraMetricsEnabled()) {
						interactionExtraMetrics.updateFinishedInteraction(interaction);
					}

					if (
						getConfig()?.extraSearchPageInteraction?.enabled &&
						interaction.ufoName === getConfig()?.extraSearchPageInteraction?.searchPageMetricName
					) {
						onSearchPageInteractionComplete(interactionId, interaction);
					}
					activeSubmitted = true;
				}

				postInteraction();
			}
			if (
				isInteractionExtraMetricsEnabled() &&
				noMoreActiveHolds &&
				noMoreActiveMetricVariantHolds
			) {
				const data = {
					...interaction,
					end: endTime!,
				};
				interactionExtraMetrics.onInteractionComplete(interactionId, data);
			}
		}
	}
}

function callCancelCallbacks(interaction: InteractionMetrics) {
	interaction.cancelCallbacks.reverse().forEach((cancelCallback) => {
		cancelCallback();
	});
}

export function abort(interactionId: string, abortReason: AbortReasonType): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			interaction.end3p = performance.now();
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: abortReason === 'timeout' ? 'timeout_expired' : 'page_unloaded',
				timestamp: interaction.end3p,
				activeHoldCount: interaction.hold3pActive?.size ?? 0,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			if (isInteractionExtraMetricsEnabled()) {
				interactionExtraMetrics.stopAll(interactionId);
			}
			return;
		}

		callCancelCallbacks(interaction);
		interaction.abortReason = abortReason;
		addLifecycleObservation(interaction, {
			type: abortReason === 'timeout' ? 'timeout_expired' : 'page_unloaded',
			timestamp: performance.now(),
			activeHoldCount: interaction.holdActive.size + (interaction.hold3pActive?.size ?? 0),
		});
		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();

		if (isInteractionExtraMetricsEnabled()) {
			interactionExtraMetrics.stopAll(interactionId);
		}
	}
}

export function abortByNewInteraction(interactionId: string, interactionName: string): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			// Set end3p to current time, but ensure it's at least interaction.end
			interaction.end3p = performance.now();
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: 'new_interaction_started',
				timestamp: interaction.end3p,
				triggerName: interactionName,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			if (isInteractionExtraMetricsEnabled()) {
				interactionExtraMetrics.stopAll(interactionId);
			}
			return;
		}

		callCancelCallbacks(interaction);
		interaction.abortReason = 'new_interaction';
		interaction.abortedByInteractionName = interactionName;
		addLifecycleObservation(interaction, {
			type: 'new_interaction_started',
			timestamp: performance.now(),
			triggerName: interactionName,
		});
		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();

		if (isInteractionExtraMetricsEnabled()) {
			interactionExtraMetrics.stopAll(interactionId);
		}
	} else {
		if (fg('platform_reset_post_interaction_on_new_interaction')) {
			// post-interaction log is active after interaction is aborted by new one
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();
		}
	}
}

export function abortAll(abortReason: AbortReasonType, abortedByInteractionName?: string): void {
	const activeInteraction = getActiveInteraction();
	const finishInteractions = getFinishInteractionOnTransition();
	interactions.forEach((interaction, interactionId) => {
		const isActiveInteraction = activeInteraction === interaction;
		let hasFinished = interaction.holdActive.size === 0;

		if (
			isActiveInteraction &&
			abortReason === 'transition' &&
			interaction.type === 'press' &&
			finishInteractions?.includes(interaction.ufoName)
		) {
			hasFinished = true;
		}

		const shouldUseRawDataThirdParty = shouldUseRawDataThirdPartyBehavior(
			interaction.ufoName,
			interaction.type,
		);
		const noMoreActiveHolds = interaction.holdActive.size === 0;
		const has3pHoldsActive = interaction.hold3pActive && interaction.hold3pActive.size > 0;
		const shouldUseSeparatedMetricVariantBehavior =
			shouldUseRawDataThirdParty || hasActiveMetricVariantCategory(interaction, 'gen-ai');

		// If only extended metric variant holds are active, finish as successful instead of aborting
		if (shouldUseSeparatedMetricVariantBehavior && noMoreActiveHolds && has3pHoldsActive) {
			const endTime = interaction.end !== 0 ? interaction.end : performance.now();
			interaction.end3p = performance.now();
			recordActiveMetricVariantCategoryEnds(interaction, interaction.end3p);
			addLifecycleObservation(interaction, {
				type: abortReason === 'transition' ? 'transition_started' : 'new_interaction_started',
				timestamp: interaction.end3p,
				triggerName: abortedByInteractionName,
			});
			ensureMetricWindows(interaction);
			finishInteraction(interactionId, interaction, endTime);
			postInteractionLog.reset();
			postInteractionLog.stopVCObserver();

			if (isInteractionExtraMetricsEnabled()) {
				interactionExtraMetrics.stopAll(interactionId);
			}
			return;
		}

		if (!hasFinished) {
			callCancelCallbacks(interaction);
			interaction.abortReason = abortReason;
			if (abortedByInteractionName != null) {
				interaction.abortedByInteractionName = abortedByInteractionName;
			}
			addLifecycleObservation(interaction, {
				type: abortReason === 'transition' ? 'transition_started' : 'new_interaction_started',
				timestamp: performance.now(),
				triggerName: abortedByInteractionName,
			});
		}

		finishInteraction(interactionId, interaction);
		postInteractionLog.reset();
		postInteractionLog.stopVCObserver();

		if (isInteractionExtraMetricsEnabled()) {
			interactionExtraMetrics.stopAll(interactionId);
		}
	});
}

export function addOnCancelCallback(id: string, cancelCallback: () => void): void {
	const interaction = interactions.get(id);

	interaction?.cancelCallbacks.push(cancelCallback);
}

export function addNewInteraction(
	interactionId: string,
	ufoName: string,
	type: InteractionType,
	startTime: number,
	rate: number,
	labelStack: LabelStack | null,
	routeName?: string | null,
	trace: TraceIdContext | null = null,
): void {
	if (isInteractionExtraMetricsEnabled()) {
		interactionExtraMetrics.reset();
	}
	postInteractionLog.reset();
	let vcObserver: VCObserverInterface | undefined;
	let previousTime = startTime;

	let timeoutTime = getInteractionTimeout(ufoName);
	const timerID: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
		abort(interactionId, 'timeout');
	}, timeoutTime);

	function changeTimeout(this: InteractionMetrics, newTime: number) {
		// we compare if the time left is lower than the new time to no
		// extend the timeout beyond the initial waiting time
		const currentTime = performance.now();
		const timeLeft = timeoutTime - (currentTime - previousTime);
		if (timeLeft < newTime) {
			return;
		}
		clearTimeout(this.timerID);
		const newTimerID: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
			abort(interactionId, 'timeout');
		}, newTime);
		timeoutTime = newTime;
		previousTime = currentTime;
		this.timerID = newTimerID;
	}

	const addFeatureFlagsToInteraction = coinflip(getCapabilityRate('feature_flag_access'));

	const config = getConfig();

	const searchPageConfig = {
		enableSmartAnswersMutations: config?.extraSearchPageInteraction?.enabled,
		searchPageRoute: config?.extraSearchPageInteraction?.searchPageRoute,
	};

	if (config && config.vc) {
		const vcOptions = {
			heatmapSize: config.vc.heatmapSize,
			oldDomUpdates: config.vc.oldDomUpdates,
			devToolsEnabled: config.vc.devToolsEnabled,
			// Use centralised getter so the FedRAMP override (forces every
			// selector field to false) is applied consistently.
			selectorConfig: getSelectorConfig(),
			ssrEnablePageLayoutPlaceholder: config.vc.ssrEnablePageLayoutPlaceholder,
			trackLayoutShiftOffenders: config.vc.trackLayoutShiftOffenders,
			searchPageConfig,
		};
		vcObserver = newVCObserver(vcOptions);
	}

	const priorAccessedFg = type === 'press' ? {} : Object.fromEntries(allFeatureFlagsAccessed);

	const metrics: InteractionMetrics = {
		id: interactionId,
		start: startTime,
		end: 0,
		ufoName,
		type,
		previousInteractionName: PreviousInteractionLog.name,
		isPreviousInteractionAborted: PreviousInteractionLog.isAborted === true,
		marks: [],
		customData: [],
		cohortingCustomData: new Map(),
		customTimings: [],
		spans: [],
		requestInfo: [],
		reactProfilerTimings: [],
		holdInfo: [],
		holdActive: new Map(),
		// measure when we execute this code
		// from this, we can measure the input delay -
		// how long the browser took to hand execution back to JS)
		measureStart: performance.now(),
		rate,
		cancelCallbacks: [],
		metaData: {},
		errors: [],
		apdex: [],
		labelStack,
		routeName: routeName ?? ufoName,
		featureFlags: addFeatureFlagsToInteraction
			? {
					prior: priorAccessedFg,
					during: {},
				}
			: undefined,
		knownSegments: [],
		cleanupCallbacks: [],
		awaitReactProfilerCount: 0,
		redirects: [],
		timerID,
		changeTimeout,
		trace,
		vcObserver,
		hold3pActive: new Map(),
		hold3pInfo: [],
		minorInteractions: [],
	};
	if (addFeatureFlagsToInteraction) {
		currentFeatureFlagsAccessed.clear();
	}
	interactions.set(interactionId, metrics);

	const segmentObserver: SegmentObserver = {
		onAdd(segment) {
			metrics.knownSegments.push(segment);
		},
		onRemove() {},
	};
	addSegmentObserver(segmentObserver);
	metrics.cleanupCallbacks.push(() => {
		removeSegmentObserver(segmentObserver);
	});
	metrics.cleanupCallbacks.push(() => {
		clearTimeout(metrics.timerID);
	});
	// Add cleanup for per-interaction VC observer
	if (vcObserver) {
		metrics.cleanupCallbacks.push(() => {
			vcObserver.stop(ufoName);
		});
	}
	const awaitBM3TTIList = getAwaitBM3TTIList();
	if (awaitBM3TTIList.includes(ufoName)) {
		addHoldByID(interactionId, [], ufoName, ufoName, true);
	}
	if (type === 'transition' || type === 'page_load') {
		// Use per-interaction VC observer if available, otherwise fall back to global
		const observer = vcObserver;
		if (observer) {
			observer.start({ startTime, experienceKey: ufoName });
		}
		// Start post interaction observer for all if config is enabled
		// in case ufoName is updated at later time
		if (getConfig()?.postInteractionLog?.enabled) {
			postInteractionLog.startVCObserver({ startTime });
		}
		if (isInteractionExtraMetricsEnabled()) {
			interactionExtraMetrics.startVCObserver({ startTime }, interactionId);
		}
	}

	if (type === 'press') {
		// Use per-interaction VC observer if available, otherwise fall back to global
		const observer = vcObserver;
		if (observer) {
			observer.start({ startTime, experienceKey: ufoName });
		}
	}
}

export function addBrowserMetricEvent(event: BM3Event): void {
	const interaction = getActiveInteraction();
	if (interaction) {
		interaction.legacyMetrics = interaction.legacyMetrics || [];
		interaction.legacyMetrics.push(event);

		if (
			(interaction.type === 'page_load' || interaction.type === 'transition') &&
			event.config?.type === 'PAGE_LOAD'
		) {
			removeHoldByID(interaction.id, interaction.ufoName);
		}
	}
}

export function addApdexToAll(apdex: ApdexType): void {
	interactions.forEach((interaction, key) => {
		interaction.apdex.push(apdex);
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${apdex.key} [bm3_tti]`, {
				start: apdex.startTime ?? interaction.start,
				end: apdex.stopTime,
				detail: {
					devtools: {
						track: 'main metrics',
						trackGroup: '🛸 reactUFO metrics',
						color: 'primary-dark',
					},
				},
			});

			performance.mark(`[bm3] TTI`, {
				startTime: apdex.stopTime,
				detail: {
					devtools: {
						dataType: 'marker',
					},
				},
			});
		} catch {
			// do nothing
		}
		if (interaction.type === 'page_load' || interaction.type === 'transition') {
			removeHoldByID(key, interaction.ufoName);
		}
	});
}

export function addApdex(
	interactionId: string,
	apdexInfo: {
		key: string;
		stopTime: number;
		startTime?: number;
		labelStack?: LabelStack;
	},
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.apdex.push(apdexInfo);
		try {
			// for Firefox 102 and older
			performance.measure(`🛸 ${apdexInfo.key} [bm3_tti]`, {
				start: apdexInfo.startTime ?? interaction.start,
				end: apdexInfo.stopTime,
				detail: {
					devtools: {
						track: 'main metrics',
						trackGroup: '🛸 reactUFO metrics',
						color: 'primary-dark',
					},
				},
			});

			performance.mark(`[bm3] TTI`, {
				startTime: apdexInfo.stopTime,
				detail: {
					devtools: {
						dataType: 'marker',
					},
				},
			});
		} catch {
			// do nothing
		}
		if (interaction.type === 'page_load' || interaction.type === 'transition') {
			removeHoldByID(interactionId, interaction.ufoName);
		}
	}
}

export function addRequestInfo(
	interactionId: string,
	labelStack: LabelStack,
	requestInfo: RequestInfo,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.requestInfo.push({
			labelStack,
			...requestInfo,
		});
	}
}

function isSegmentLabel(obj: any): obj is SegmentLabel {
	return obj && typeof obj.name === 'string' && typeof obj.segmentId === 'string';
}

function getSegmentCacheKey(labelStack: LabelStack) {
	return labelStack
		.map((l) => {
			if (isSegmentLabel(l)) {
				return `${l.name}_${l.segmentId}`;
			}
			return l.name;
		})
		.join('|');
}

export function addSegment(labelStack: LabelStack): void {
	const key = getSegmentCacheKey(labelStack);
	const existingSegment = segmentCache.get(key);
	if (!existingSegment) {
		const segmentInfo: SegmentInfo = { labelStack };
		segmentCache.set(key, segmentInfo);
		segmentObservers.forEach((observer) => {
			observer.onAdd(segmentInfo);
		});
	}
}

export function removeSegment(labelStack: LabelStack): void {
	const key = getSegmentCacheKey(labelStack);
	const segmentInfo = segmentCache.get(key);

	if (segmentInfo) {
		segmentCache.delete(JSON.stringify(labelStack));

		const cacheKey = stringifyLabelStackFully(labelStack);
		segmentUnmountCache.set(cacheKey, (segmentUnmountCache.get(cacheKey) || 0) + 1);

		segmentObservers.forEach((observer) => {
			observer.onRemove(segmentInfo);
		});
	}
}

export function addRedirect(
	interactionId: string,
	fromUfoName: string,
	nextUfoName: string,
	nextRouteName: string,
	time: number,
): void {
	const interaction = interactions.get(interactionId);
	if (interaction != null) {
		interaction.ufoName = nextUfoName;
		interaction.routeName = nextRouteName;
		interaction.redirects.push({ fromInteractionName: fromUfoName, time });

		if (isPerformanceTracingEnabled()) {
			const prevRedirect = interaction.redirects.at(-2);
			try {
				// for Firefox 102 and older
				performance.measure(`🛸 ${nextUfoName} [redirect]`, {
					start: prevRedirect?.time ?? interaction.start,
					end: time,
					detail: {
						devtools: {
							track: '🛸 reactUFO detailed timings',
							color: 'tertiary',
						},
					},
				});
			} catch {
				// do nothing
			}
		}
	}
}

declare global {
	interface Window {
		__REACT_UFO_ENABLE_PERF_TRACING?: boolean;
		__UFO_COMPACT_PAYLOAD__?: boolean;
		__CRITERION__?: {
			addFeatureFlagAccessed?: (flagName: string, flagValue: FeatureFlagValue) => void;
			addUFOHold?: (id: string, name: string, labelStack: string, startTime: number) => void;
			removeUFOHold?: (id: string) => void;
			getFeatureFlagOverride?: (flagName: string) => boolean | undefined;
			getExperimentValueOverride?: <T>(experimentName: string, parameterName: string) => T;
		};
	}
}

export const interactionSpans: Span[] = [];

const defaultLabelStack = [{ name: 'custom' }];

export function addCustomSpans(
	name: string,
	start: number,
	end: number = performance.now(),
	size = 0,
	labelStack: LabelStack = defaultLabelStack,
): void {
	const customSpan: Span = {
		type: 'custom',
		name,
		start,
		end,
		labelStack,
		size,
	};

	interactionSpans.push(customSpan);
}
